import {NodePort} from './workflow.models';
import {RenderedNode} from './workflow-canvas.models';

/** Layout constants – must match the CSS in flow-canvas.component.scss */
export const NODE_WIDTH = 400;
export const NODE_BORDER = 1;                // .fc-node  border: 1px solid
export const HEADER_HEIGHT = 32;             // .fc-node__header  padding 8 + content 16 + padding 8
export const BODY_PADDING_TOP = 8;           // .fc-node__body  padding-top
export const BODY_PADDING_BOTTOM = 8;        // .fc-node__body  padding-bottom
export const PORT_ROW_HEIGHT = 18;           // .fc-port__label  line-height: 18px
export const PORT_ROW_GAP = 8;              // .fc-node__ports-left/right  gap: 8px
export const PORT_STRIDE = PORT_ROW_HEIGHT + PORT_ROW_GAP; // 26 – distance between port centers

/** Portmaße – es gibt nur noch Flow-Ports. */
export const FLOW_PORT_HEIGHT = 12;          // .fc-port__dot  height: 12px (labelloser ▶-Port)
export const FLOW_PORT_MARGIN_BOTTOM = 4;    // .fc-port--flow  margin-bottom: 4px

/** Grid size for snap-to-grid – matches the CSS dot-grid (background-size: 24px 24px) */
export const GRID_SIZE = 24;

/** Snap a value to the nearest grid line */
export function snapToGrid(value: number, gridSize: number = GRID_SIZE): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Ein Port rendert als bloßer Punkt (ohne `.fc-port__label`), wenn sein Name der generische
 * ▶-Pfeil ist. Benannte Zweige (Dann/Sonst bzw. die Fälle einer Verzweigung) zeigen ein Label
 * neben dem Punkt; dann bestimmt die 18px-Zeilenhöhe des Labels die Zeile, nicht der 12px-Punkt.
 */
function isBarePort(port: NodePort | undefined): boolean {
  return port?.name === '▶';
}

/**
 * Höhe einer Portzeile in der Flex-Spalte (ohne den 8px-Abstand).
 *
 * Seit es nur noch Flow-Ports gibt, trägt JEDE Zeile im Markup `.fc-port--flow` und damit deren
 * `margin-bottom: 4px`. Die Konstante und die CSS-Regel müssen zusammen bleiben — wird nur eine
 * geändert, verschieben sich alle Portkoordinaten gegen das DOM.
 */
function portRowHeight(port: NodePort | undefined): number {
  return (isBarePort(port) ? FLOW_PORT_HEIGHT : PORT_ROW_HEIGHT) + FLOW_PORT_MARGIN_BOTTOM;
}

/** Total column height of a port list (rows + gaps) – supports multiple flow ports */
function portColumnHeight(ports: NodePort[]): number {
  if (ports.length === 0) return 0;
  const rows = ports.reduce((sum, p) => sum + portRowHeight(p), 0);
  return rows + (ports.length - 1) * PORT_ROW_GAP;
}

/** Vertical center of a port dot relative to the body top – derived from the actual port list */
function portCenterOffset(ports: NodePort[], portIndex: number): number {
  let y = 0;
  for (let i = 0; i < portIndex; i++) {
    y += portRowHeight(ports[i]) + PORT_ROW_GAP;
  }
  return y + (isBarePort(ports[portIndex]) ? FLOW_PORT_HEIGHT / 2 : PORT_ROW_HEIGHT / 2);
}

/**
 * Compute the height of a node card based on its port lists.
 *
 * `headerHeight` ist ein Parameter, weil der Kopf eine zweite Zeile bekommen kann (die
 * Konfigurations-Zusammenfassung, siehe {@link headerHeightFor}). Er MUSS mit dem Wert am
 * RenderedNode übereinstimmen: `getOutputPortPoint`/`getInputPortPoint` rechnen die
 * Portkoordinaten aus `node.headerHeight`, eine Abweichung verschiebt sämtliche Kanten.
 */
export function computeNodeHeight(
  inputPorts: NodePort[],
  outputPorts: NodePort[],
  headerHeight: number = HEADER_HEIGHT,
): number {
  const columnHeight = Math.max(portColumnHeight(inputPorts), portColumnHeight(outputPorts));
  const bodyHeight = BODY_PADDING_TOP + columnHeight + BODY_PADDING_BOTTOM;
  return 2 * NODE_BORDER + headerHeight + bodyHeight;
}

/** Zeilenhöhe der Zusammenfassung im Kopf (.fc-node__summary, 11px bei line-height 14px). */
export const SUMMARY_LINE_HEIGHT = 14;

/** Kopfhöhe mit oder ohne Zusammenfassungs-Zeile. */
export function headerHeightFor(hasSummary: boolean): number {
  return HEADER_HEIGHT + (hasSummary ? SUMMARY_LINE_HEIGHT : 0);
}

/** Get the absolute position of an output port dot (right edge of node) */
export function getOutputPortPoint(node: RenderedNode, portIndex: number): { x: number; y: number } {
  const baseY = node.y + NODE_BORDER + node.headerHeight + BODY_PADDING_TOP;
  return {x: node.x + node.width, y: baseY + portCenterOffset(node.outputPorts, portIndex)};
}

/** Get the absolute position of an input port dot (left edge of node) */
export function getInputPortPoint(node: RenderedNode, portIndex: number): { x: number; y: number } {
  const baseY = node.y + NODE_BORDER + node.headerHeight + BODY_PADDING_TOP;
  return {x: node.x, y: baseY + portCenterOffset(node.inputPorts, portIndex)};
}

/** Build a horizontal bezier curve SVG path */
export function buildBezierPath(sx: number, sy: number, tx: number, ty: number): string {
  const dx = Math.abs(tx - sx);
  const offset = Math.max(50, dx * 0.4);
  return `M${sx},${sy} C${sx + offset},${sy} ${tx - offset},${ty} ${tx},${ty}`;
}

/** Calculate SVG canvas bounds to encompass all nodes + generous padding (supports negative coords) */
export function calculateSvgBounds(nodes: RenderedNode[]): {
  minX: number;
  minY: number;
  width: number;
  height: number
} {
  if (nodes.length === 0) {
    return {minX: -2000, minY: -2000, width: 8000, height: 8000};
  }

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.width);
    maxY = Math.max(maxY, n.y + n.height);
  }

  const padding = 2000;
  minX -= padding;
  minY -= padding;
  maxX += padding;
  maxY += padding;

  return {
    minX: Math.min(minX, -2000),
    minY: Math.min(minY, -2000),
    width: Math.max(maxX - Math.min(minX, -2000), 6000),
    height: Math.max(maxY - Math.min(minY, -2000), 6000),
  };
}

/** @deprecated Use calculateSvgBounds instead – kept for backward compat */
export function calculateSvgSize(nodes: RenderedNode[]): { width: number; height: number } {
  const bounds = calculateSvgBounds(nodes);
  return {width: bounds.width, height: bounds.height};
}

// ─── Auto-Layout (hierarchical left-to-right) ─────────────────────────

interface LayoutEdge {
  source: string;
  target: string;
}

/**
 * Compute new positions for a hierarchical left-to-right layout.
 * Returns a map of nodeId → { x, y }.
 *
 * Algorithm:
 * 1. Build adjacency from edges
 * 2. Topological sort (Kahn's algorithm, with fallback for cycles)
 * 3. Assign layers (longest-path layering from sources)
 * 4. Order nodes within layers to minimize crossings (barycenter heuristic)
 * 5. Assign x/y positions with configurable gaps
 */
export function computeAutoLayout(
  nodes: RenderedNode[],
  edges: LayoutEdge[],
  options?: { gapX?: number; gapY?: number; startX?: number; startY?: number }
): Map<string, { x: number; y: number }> {
  const gapX = options?.gapX ?? 120;   // horizontal gap between layers
  const gapY = options?.gapY ?? 40;    // vertical gap between nodes in same layer
  const startX = options?.startX ?? 60;
  const startY = options?.startY ?? 60;

  const nodeById = new Map<string, RenderedNode>();
  for (const n of nodes) nodeById.set(n.id, n);

  // Build adjacency
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  for (const n of nodes) {
    outgoing.set(n.id, []);
    incoming.set(n.id, []);
    inDegree.set(n.id, 0);
  }
  for (const e of edges) {
    if (!nodeById.has(e.source) || !nodeById.has(e.target)) continue;
    outgoing.get(e.source)!.push(e.target);
    incoming.get(e.target)!.push(e.source);
    inDegree.set(e.target, (inDegree.get(e.target) ?? 0) + 1);
  }

  // ─── Topological sort (Kahn) ───
  const sorted: string[] = [];
  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }
  const tempDegree = new Map(inDegree);
  while (queue.length > 0) {
    const id = queue.shift()!;
    sorted.push(id);
    for (const next of outgoing.get(id) ?? []) {
      const d = (tempDegree.get(next) ?? 1) - 1;
      tempDegree.set(next, d);
      if (d === 0) queue.push(next);
    }
  }
  // Handle any remaining nodes (cycles) → just append them
  for (const n of nodes) {
    if (!sorted.includes(n.id)) sorted.push(n.id);
  }

  // ─── Layer assignment (longest-path from sources) ───
  const layer = new Map<string, number>();
  for (const id of sorted) {
    const parents = incoming.get(id) ?? [];
    if (parents.length === 0) {
      layer.set(id, 0);
    } else {
      let maxParentLayer = 0;
      for (const p of parents) {
        maxParentLayer = Math.max(maxParentLayer, (layer.get(p) ?? 0) + 1);
      }
      layer.set(id, maxParentLayer);
    }
  }

  // ─── Group nodes by layer ───
  const layers = new Map<number, string[]>();
  for (const [id, l] of layer) {
    if (!layers.has(l)) layers.set(l, []);
    layers.get(l)!.push(id);
  }

  // ─── Order within layers (barycenter heuristic, 2 passes) ───
  const maxLayer = Math.max(...layers.keys(), 0);
  for (let pass = 0; pass < 2; pass++) {
    for (let l = 1; l <= maxLayer; l++) {
      const nodesInLayer = layers.get(l);
      if (!nodesInLayer || nodesInLayer.length <= 1) continue;

      // Compute barycenter: average position of connected nodes in previous layer
      const prevLayer = layers.get(l - 1) ?? [];
      const prevIndex = new Map<string, number>();
      prevLayer.forEach((id, i) => prevIndex.set(id, i));

      const bary = new Map<string, number>();
      for (const id of nodesInLayer) {
        const parents = (incoming.get(id) ?? []).filter(p => prevIndex.has(p));
        if (parents.length > 0) {
          const avg = parents.reduce((sum, p) => sum + (prevIndex.get(p) ?? 0), 0) / parents.length;
          bary.set(id, avg);
        } else {
          bary.set(id, nodesInLayer.indexOf(id));
        }
      }

      nodesInLayer.sort((a, b) => (bary.get(a) ?? 0) - (bary.get(b) ?? 0));
    }
  }

  // ─── Assign positions ───
  const result = new Map<string, { x: number; y: number }>();

  // Compute layer X positions (based on max node width per layer)
  const layerX: number[] = [];
  let currentX = startX;
  for (let l = 0; l <= maxLayer; l++) {
    layerX.push(currentX);
    const nodesInLayer = layers.get(l) ?? [];
    let maxWidth = NODE_WIDTH;
    for (const id of nodesInLayer) {
      const n = nodeById.get(id);
      if (n) maxWidth = Math.max(maxWidth, n.width);
    }
    currentX += maxWidth + gapX;
  }

  // Compute Y positions: align nodes by their top position so that
  // flow edges (at the top of each node) form a nearly straight horizontal line.
  // Flow-Kanten verlaufen von der obersten Portzeile without passing
  // through intermediate nodes.

  for (let l = 0; l <= maxLayer; l++) {
    const nodesInLayer = layers.get(l) ?? [];

    if (nodesInLayer.length === 1) {
      // Single node in layer: align its flow port to the baseline
      const id = nodesInLayer[0];
      result.set(id, {x: layerX[l], y: Math.round(startY)});
    } else {
      // Multiple nodes in layer: position the first node at baseline,
      // subsequent nodes below with gap
      let y = startY;
      for (const id of nodesInLayer) {
        const n = nodeById.get(id);
        const h = n ? n.height : 60;
        result.set(id, {x: layerX[l], y: Math.round(y)});
        y += h + gapY;
      }
    }
  }

  return result;
}

// ─── Auto-Panning beim Ziehen einer Kante ─────────────────────────────────────

/** Sichtbarer Ausschnitt der Canvas in Bildschirmkoordinaten (Teilmenge von DOMRect). */
export interface ViewportRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Geschwindigkeit, mit der die Ansicht mitwandern soll, wenn der Zeiger nah an den Rand kommt.
 *
 * Der Wert ist eine Verschiebung des Inhalts in Bildschirm-Pixeln pro Sekunde und wächst linear
 * mit der Eindringtiefe in den Randstreifen: am inneren Rand null, ganz außen `maxSpeed`. Ein
 * harter Ein/Aus-Schalter ließe die Ansicht springen, sobald der Zeiger die Grenze streift.
 *
 * Vorzeichen: Am RECHTEN Rand muss der Inhalt nach LINKS wandern (negatives x), damit rechts
 * liegende Knoten ins Bild kommen – die Ansicht bewegt sich also gegenläufig zur Zeigerrichtung.
 * Liegt der Zeiger außerhalb des Ausschnitts, gilt die volle Geschwindigkeit.
 */
export function autoPanSpeed(
  rect: ViewportRect,
  pointerX: number,
  pointerY: number,
  band: number,
  maxSpeed: number,
): { x: number; y: number } {
  if (band <= 0) return {x: 0, y: 0};
  const depth = (distanceToEdge: number) => Math.min(Math.max(band - distanceToEdge, 0) / band, 1);
  return {
    x: (depth(pointerX - rect.left) - depth(rect.right - pointerX)) * maxSpeed,
    y: (depth(pointerY - rect.top) - depth(rect.bottom - pointerY)) * maxSpeed,
  };
}

/**
 * Breite, die das schwebende Konfigurations-Panel rechts über der Canvas verdeckt (Panelbreite
 * 400 px + 24 px Abstand, siehe `workflow-editor.component.html`).
 *
 * Die Canvas liegt VOLLSTÄNDIG unter dem Panel – ihr `getBoundingClientRect()` reicht also bis
 * zum Fensterrand, obwohl der rechte Teil nicht sichtbar und mit der Maus nicht erreichbar ist.
 * Wer mit dem sichtbaren Ausschnitt rechnet (Auto-Panning, „Ansicht einpassen"), muss diesen
 * Betrag abziehen.
 */
export const SIDEBAR_OVERLAY_WIDTH = 424;
